import { ButtonDropdown, DatePicker } from "@rio-cloud/rio-uikit";
import { useState } from "react";
import { useIntl } from "react-intl";

export interface DateRangeValue {
    startDate?: string;
    endDate?: string;
}

interface Props extends DateRangeValue {
    title: string;
    onChange: (value: DateRangeValue) => void;
}

const formatDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

const parseDate = (value?: string) => {
    if (!value) return undefined;

    const [year, month, day] = value.split("T")[0].split("-").map(Number);
    if (!year || !month || !day) return undefined;

    return new Date(year, month - 1, day);
};

const getDateFromPicker = (value: unknown) => {
    if (!value || typeof value === "string") return undefined;

    if (typeof (value as { toDate?: unknown }).toDate === "function") {
        return (value as { toDate: () => Date }).toDate();
    }

    return value instanceof Date ? value : undefined;
};

const DateRangeFilter = ({ title, startDate, endDate, onChange }: Props) => {
    const intl = useIntl();
    const [endDateError, setEndDateError] = useState(false);

    const applyPreset = (startOffset: number, endOffset = 0) => {
        const start = new Date();
        const end = new Date();
        start.setDate(start.getDate() + startOffset);
        end.setDate(end.getDate() + endOffset);

        onChange({
            startDate: formatDate(start),
            endDate: formatDate(end),
        });
    };

    const presetItems = [
        {
            value: intl.formatMessage({ id: "intl-msg:tas.support.filters.today" }),
            onSelect: () => applyPreset(0),
        },
        {
            value: intl.formatMessage({ id: "intl-msg:tas.support.filters.lastTwentyFourHours" }),
            onSelect: () => applyPreset(-1),
        },
        {
            value: intl.formatMessage({ id: "intl-msg:tas.support.filters.yesterday" }),
            onSelect: () => applyPreset(-1, -1),
        },
        {
            value: intl.formatMessage({ id: "intl-msg:tas.support.filters.lastThreeDays" }),
            onSelect: () => applyPreset(-2),
        },
        {
            value: intl.formatMessage({ id: "intl-msg:tas.support.filters.lastTenDays" }),
            onSelect: () => applyPreset(-9),
        },
        { divider: true },
        {
            value: (
                <span className="text-color-danger">
                    {intl.formatMessage({ id: "intl-msg:tas.support.filters.clearDate" })}
                </span>
            ),
            onSelect: () => onChange({}),
        },
    ];

    const handleStartDateChange = (value: unknown) => {
        if (typeof value === "string" && value.length) return;

        const nextStartDate = getDateFromPicker(value);
        if (nextStartDate && endDate && nextStartDate > new Date(`${endDate}T23:59:59`)) return;

        onChange({
            startDate: nextStartDate ? formatDate(nextStartDate) : undefined,
            endDate,
        });
    };

    const handleEndDateChange = (value: unknown) => {
        if (typeof value === "string" && value.length) return;

        const nextEndDate = getDateFromPicker(value);
        if (nextEndDate && startDate && nextEndDate < new Date(`${startDate}T00:00:00`)) {
            setEndDateError(true);
            return;
        }

        setEndDateError(false);
        onChange({
            startDate,
            endDate: nextEndDate ? formatDate(nextEndDate) : undefined,
        });
    };

    return (
        <div className="display-flex flex-row">

            <ButtonDropdown
                className="align-self-end margin-bottom-15"
                title={<span className="rioglyph rioglyph-settings" aria-hidden="true" />}
                iconOnly
                items={presetItems}
            />


            <div className="display-flex flex-1-1">
                <div className={`form-group margin-0 flex-1-1 ${!startDate && endDate ? "has-feedback has-error" : ""}`}>
                    <label htmlFor="support-opening-date-start">{title}</label>
                    <DatePicker
                        id="support-opening-date-start"
                        value={parseDate(startDate)}
                        onChange={handleStartDateChange}
                        locale={intl.locale}
                        dateFormat="DD-MM-YYYY"
                        timeFormat={false}
                        mandatory={false}
                        clearableInput
                    />
                </div>

                <span className="text-color-gray align-self-center">
                    {intl.formatMessage({ id: "intl-msg:tas.support.filters.until" })}
                </span>

                <div className={`form-group margin-0 flex-1-1 ${startDate && !endDate || endDateError ? "has-feedback has-error" : ""}`}>
                    <label htmlFor="support-opening-date-end">&nbsp;</label>
                    <DatePicker
                        id="support-opening-date-end"
                        value={parseDate(endDate)}
                        onChange={handleEndDateChange}
                        locale={intl.locale}
                        dateFormat="DD-MM-YYYY"
                        timeFormat={false}
                        mandatory={false}
                        clearableInput
                        alignRight

                    />
                    {endDateError && (
                        <span className="help-block">
                            {intl.formatMessage({ id: "intl-msg:tas.support.filters.invalidDateRange" })}
                        </span>
                    )}
                </div>
            </div>
        </div>
    );
};

export default DateRangeFilter;
